import { useEffect, useState, useCallback } from 'react';
import { Users, Clock, Building2, Ticket, ArrowRight, Tag } from 'lucide-react';
import { Link } from 'react-router-dom';
import { BackgroundSelector } from '../BackgroundSelector';
import { SectionRenderer } from '../sections/SectionRenderer';
import { Section, SectionType } from '../../types/Section';
import { useEventDetails } from '../../../hooks/useEventDetails';
import { HeroData, PageSection, EventDetails, ContentPage } from '../../../types/eventDetails.types';
import { useEventContext } from '../../contexts/EventContext';
import { KeywordsModal } from './KeywordsModal';
import { EventDetailsModal } from './EventDetailsModal';
import { EventDateLocationDisplay } from '../../../components/common/EventDateLocationDisplay';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { useParticipantCheck } from '../../../hooks/useParticipantCheck';

export const DetailsPage = () => {
  const { eventSlug, style: contextStyle } = useEventContext();
  const { data: eventDetails, isLoading: eventLoading, error: eventError } = useEventDetails(eventSlug);
  const { currentUser } = useCurrentUser();
  const { data: participantCheck } = useParticipantCheck(eventSlug);
  const [background, setBackground] = useState('bg-gray-50');
  const [showMobileFooter, setShowMobileFooter] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showKeywordsModal, setShowKeywordsModal] = useState(false);
  const [showEventDetailsModal, setShowEventDetailsModal] = useState(false);

  // Check if user is a participant using the lightweight check
  const isParticipant = participantCheck?.isParticipant || false;

  // Update page title with custom navigation name
  useEffect(() => {
    if (eventDetails) {
      const detailsName = eventDetails.eventOptions?.detailsName || 'Details';
      document.title = `${detailsName} - ${eventDetails.name}`;
    }
  }, [eventDetails]);

  // Use style from context (passed from server) or from API response
  const hasStyle = contextStyle || eventDetails?.style;

  // Apply background class to body when no style exists
  useEffect(() => {
    if (!hasStyle && background) {
      // Apply the background class to the root app div's parent
      const appDiv = document.querySelector('.min-h-screen');
      if (appDiv) {
        // Remove any existing bg- classes
        appDiv.className = appDiv.className.replace(/bg-\S+/g, '');
        // Add the new background class
        appDiv.classList.add(background);
      }
    }
  }, [background, hasStyle]);

  // Function to generate hero background style based on type
  const getHeroBackgroundStyle = (heroData: any) => {
    if (!heroData) {
      return {};
    }

    // If there's a background image, don't apply color/gradient to the container
    if (heroData.backgroundImage) {
      return {};
    }

    switch (heroData.backgroundType) {
      case 'color':
        return { backgroundColor: heroData.backgroundColor };
      case 'gradient':
        return {
          backgroundImage: `linear-gradient(${heroData.gradientDirection === 'to-r' ? 'to right' :
            heroData.gradientDirection === 'to-l' ? 'to left' :
              heroData.gradientDirection === 'to-b' ? 'to bottom' : 'to top'},
                            ${heroData.gradientStart}, ${heroData.gradientEnd})`
        };
      default:
        return {};
    }
  };

  // Transform API data to component format
  const transformEventDataToHero = (eventDetails: EventDetails | undefined): HeroData => {
    if (!eventDetails || !eventDetails.contentPage) {
      return {
        title: eventDetails?.name || '',
        subtitle: eventDetails?.description || '',
        backgroundType: 'color',
        backgroundColor: '#0066CC',
        textColor: '#ffffff',
        buttonBgColor: '#ffffff',
        buttonTextColor: '#0066CC',
        showDate: true,
        showTime: true,
        showLocation: true,
        ctaEnabled: true,
        ctaText: 'Register Now',
        ctaLink: eventDetails?.registrationUrl || '#',
      };
    }

    const contentPageOption = eventDetails.contentPage.contentPageOption;
    const backgroundImageUrl = eventDetails.contentPage.backgroundImageUrl;
    const eventImageUrl = eventDetails.contentPage.imageUrl || eventDetails.eventHeaderUrl;

    // Determine CTA link based on linkType
    const getCtaLink = () => {
      if (!contentPageOption?.heroCtaEnabled) return null;

      switch (contentPageOption?.heroCtaLinkType) {
        case 'registration':
          return eventDetails.registrationUrl || '#';
        case 'agenda':
          return `/agenda`;
        case 'forms':
          return `/forms`;
        case 'expo':
          return `/expo`;
        case 'custom':
          return contentPageOption?.heroCtaLink || '#';
        default:
          return contentPageOption?.heroCtaLink || eventDetails.registrationUrl || '#';
      }
    };

    const ctaNeedsHref = () => {
      if (!contentPageOption?.heroCtaEnabled) return false;

      switch (contentPageOption?.heroCtaLinkType) {
        case 'registration':
        case 'custom':
          return true;
        default:
          return false;
      }
    };

    return {
      title: contentPageOption?.heroTitle || eventDetails.name,
      subtitle: contentPageOption?.heroSubtitle || eventDetails.description,
      // Use new overlay fields if available, fallback to deprecated hero background fields
      eventImage: eventImageUrl,
      backgroundType: contentPageOption?.heroBackgroundType || 'color',
      backgroundColor: contentPageOption?.heroBackgroundColor || '#0066CC',
      backgroundImage: backgroundImageUrl,
      gradientStart: contentPageOption?.heroGradientStartColor,
      gradientEnd: contentPageOption?.heroGradientEndColor,
      gradientDirection: contentPageOption?.heroGradientDirection,
      // New overlay fields
      overlayType: contentPageOption?.overlayType || 'none',
      overlayColor: contentPageOption?.overlayColor,
      overlayGradientStart: contentPageOption?.overlayGradientStart,
      overlayGradientEnd: contentPageOption?.overlayGradientEnd,
      overlayGradientDirection: contentPageOption?.overlayGradientDirection,
      heroDetailsBackgroundCard: contentPageOption?.heroDetailsBackgroundCard || false,
      heroDetailsCardBackgroundColor: contentPageOption?.heroDetailsCardBackgroundColor || '#000000',
      heroDetailsCardOpacity: contentPageOption?.heroDetailsCardOpacity || 0.7,
      fontFamily: contentPageOption?.heroFontFamily || 'system-ui, sans-serif',
      textColor: contentPageOption?.heroTextColor || '#ffffff',
      buttonBgColor: contentPageOption?.heroButtonBgColor || '#ffffff',
      buttonTextColor: contentPageOption?.heroButtonTextColor || '#0066CC',
      overlayOpacity: contentPageOption?.heroOverlayOpacity,
      showDate: contentPageOption?.heroShowDate ?? true,
      showTime: contentPageOption?.heroShowTime ?? true,
      showLocation: contentPageOption?.heroShowLocation ?? true,
      showTimezone: contentPageOption?.heroShowTimezone ?? false,
      dateFormat: contentPageOption?.heroDateFormat || 'default',
      ctaEnabled: contentPageOption?.heroCtaEnabled ?? true,
      ctaLinkType: contentPageOption?.heroCtaLinkType,
      ctaText: contentPageOption?.heroCtaText || 'Register Now',
      ctaLink: getCtaLink(),
      ctaNeedsHref: ctaNeedsHref(),
      secondaryCtaText: contentPageOption?.heroSecondaryCtaText,
      secondaryCtaLink: contentPageOption?.heroSecondaryCtaLink,
      secondaryButtonBgColor: contentPageOption?.heroSecondaryButtonBgColor,
      secondaryButtonTextColor: contentPageOption?.heroSecondaryButtonTextColor,
      secondaryButtonBorderColor: contentPageOption?.heroSecondaryButtonBorderColor,
      showSecondaryCta: contentPageOption?.showHeroSecondaryCta || false,
    };
  };

  // Transform sections data
  const transformSections = (contentPage: ContentPage | undefined): Section[] => {
    if (!contentPage || !contentPage.sections) {
      return [];
    }

    return contentPage.sections
      .filter(section => {
        // Filter out inactive sections
        if (!section.active) return false;

        // Filter based on show_on_public_site setting
        // If sectionOption exists and show_on_public_site is explicitly false, hide it
        if (section.sectionOption && section.sectionOption.showOnPublicSite === false) {
          return false;
        }

        return true;
      })
      .sort((a, b) => a.position - b.position)
      .map((section, index) => ({
        id: section.id,
        type: section.typeOf as SectionType,
        title: section.customTitle || section.typeOf.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        content: section.details,
        fullWidth: section.sectionOption?.fullWidth,
        order: section.position || index,
        attachments: section.attachments?.map(att => ({
          id: att.id,
          name: att.name,
          fileUrl: att.fileUrl,
          url: att.fileUrl,
          fileContentType: att.fileContentType ,
          fileFileSize: att.fileFileSize,
        })),
        options: {
          background_color: section.sectionOption?.backgroundColor,
          text_color: section.sectionOption?.textColor,
          padding: section.sectionOption?.padding,
          display_style: section.sectionOption?.displayStyle,
          border_style: section.sectionOption?.borderStyle,
        }
      }));
  };

  const heroData = transformEventDataToHero(eventDetails);

  // Get font family CSS value
  const getFontFamilyValue = (font: string): string => {
    switch (font) {
      case 'helvetica': return "'Helvetica Neue', Helvetica, Arial, sans-serif";
      case 'arial': return "Arial, sans-serif";
      case 'georgia': return "Georgia, serif";
      case 'times': return "'Times New Roman', Times, serif";
      case 'courier': return "'Courier New', monospace";
      case 'verdana': return "Verdana, sans-serif";
      case 'tahoma': return "Tahoma, sans-serif";
      case 'trebuchet': return "'Trebuchet MS', sans-serif";
      case 'impact': return "Impact, sans-serif";
      default: return "system-ui, sans-serif";
    }
  };

  // Get background position CSS value
  const getBackgroundPosition = (position?: string): string => {
    switch (position) {
      case 'left': return 'left center';
      case 'center': return 'center center';
      case 'right': return 'right center';
      case 'expand': return 'center center';
      default: return 'center center';
    }
  };

  const customCss = `
    /* Custom CSS from site settings */
    .hero-title {
      text-shadow: 0 2px 4px rgba(0,0,0,0.3);
      font-family: ${getFontFamilyValue(heroData.fontFamily || 'system-ui')};
      color: ${heroData.textColor};
    }

    .hero-subtitle {
      font-family: ${getFontFamilyValue(heroData.fontFamily || 'system-ui')};
      color: ${heroData.textColor}CC;
    }

    .cta-button {
      transform: translateY(0);
      transition: all 0.3s ease;
      background-color: ${heroData.buttonBgColor};
      color: ${heroData.buttonTextColor};
      font-family: ${getFontFamilyValue(heroData.fontFamily || 'system-ui')};
    }

    .cta-button:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }

    .secondary-cta-button {
      transform: translateY(0);
      transition: all 0.3s ease;
      background-color: ${heroData.secondaryButtonBgColor || 'transparent'};
      color: ${heroData.secondaryButtonTextColor || heroData.textColor};
      border: 2px solid ${heroData.secondaryButtonBorderColor || heroData.textColor};
      font-family: ${getFontFamilyValue(heroData.fontFamily || 'system-ui')};
    }

    .secondary-cta-button:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      opacity: 0.9;
    }

    .event-image {
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
      transition: transform 0.3s ease-in-out;
    }

    .hero-section {
      border-radius: 0 0 1rem 1rem;
      overflow: hidden;
      box-shadow: 0 4px 20px -5px rgba(0, 0, 0, 0.1);
      position: relative;
    }

    /* Ensure background images and overlays respect border radius */
    .hero-section > .absolute {
      border-radius: 0 0 1rem 1rem;
    }
  `;


  // Use transformed sections from API
  const pageSections = transformSections(eventDetails?.contentPage);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let rafId: number;

    const handleScroll = () => {
      // Cancel any pending updates
      if (timeoutId) clearTimeout(timeoutId);
      if (rafId) cancelAnimationFrame(rafId);

      // Throttle updates to once every 100ms
      timeoutId = setTimeout(() => {
        rafId = requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          // Show footer when scrolling down and past the hero section
          if (currentScrollY > 400) {
            setShowMobileFooter(true);
          } else {
            setShowMobileFooter(false);
          }
          setLastScrollY(currentScrollY);
        });
      }, 100);
    };

    window.addEventListener('scroll', handleScroll, {
      passive: true
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (timeoutId) clearTimeout(timeoutId);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [lastScrollY]);

  // Sort sections by order
  const sortedSections = [...pageSections].sort((a, b) => {
    return (a.order || 0) - (b.order || 0);
  });

  // Group sections for layout purposes - fullWidth sections standalone,
  // non-fullWidth sections paired together when consecutive
  const groupSectionsForLayout = (sections: Section[]) => {
    const groups: Section[][] = [];
    let currentPair: Section[] = [];

    sections.forEach((section) => {
      if (section.fullWidth) {
        // If we have items in the current pair, add them first
        if (currentPair.length > 0) {
          groups.push([...currentPair]);
          currentPair = [];
        }
        // Add the fullWidth section as its own group
        groups.push([section]);
      } else {
        // Add to current pair
        currentPair.push(section);

        // If we have 2 items in the pair, add to groups and start a new pair
        if (currentPair.length === 2) {
          groups.push([...currentPair]);
          currentPair = [];
        }
      }
    });

    // Add any remaining sections in the current pair
    if (currentPair.length > 0) {
      groups.push([...currentPair]);
    }

    return groups;
  };

  const sectionGroups = groupSectionsForLayout(sortedSections);

  // Loading state
  if (eventLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading event details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (eventError || !eventDetails) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Event Not Found</h2>
          <p className="text-gray-600">Sorry, we couldn't find the event you're looking for.</p>
          <Link to="/" className="mt-4 inline-flex items-center gap-2 text-blue-600 hover:text-blue-700">
            <ArrowRight className="w-4 h-4 rotate-180" />
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Apply custom CSS */}
      {customCss && <style dangerouslySetInnerHTML={{ __html: customCss }} />}
      {eventDetails.contentPage?.customCss && <style dangerouslySetInnerHTML={{ __html: eventDetails.contentPage.customCss }} />}
      {eventDetails.style?.css && <style dangerouslySetInnerHTML={{ __html: eventDetails.style.css }} />}

      <div>
        {!hasStyle && <BackgroundSelector onSelect={setBackground} />}

        {/* Hero Section */}
        <div className="relative hero-section min-h-[400px] sm:min-h-[500px] mx-4 md:mx-8 lg:mx-12"
          style={heroData.backgroundType !== 'image' ? getHeroBackgroundStyle(heroData) : {}}>
          {/* Background image if exists */}
          {heroData.backgroundImage && (
            <div className="absolute inset-0">
              <img
                src={typeof heroData.backgroundImage === 'string'
                  ? heroData.backgroundImage
                  : (heroData.backgroundImage as any)?.url || ''
                }
                alt="Hero background"
                className="w-full h-full object-cover"
                onError={(e) => {
                  console.error('Failed to load background image:', heroData.backgroundImage);
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
          )}

          {/* Overlay layer based on overlayType */}
          {heroData.overlayType !== 'none' && (
            <div className="absolute inset-0" style={{
              ...(heroData.overlayType === 'color' ? {
                backgroundColor: heroData.overlayColor || 'rgba(0, 0, 0, 0.5)',
                opacity: heroData.overlayOpacity || 0.7
              } : heroData.overlayType === 'gradient' ? {
                backgroundImage: `linear-gradient(${
                  heroData.overlayGradientDirection === 'to-r' ? 'to right' :
                  heroData.overlayGradientDirection === 'to-l' ? 'to left' :
                  heroData.overlayGradientDirection === 'to-b' ? 'to bottom' : 'to top'
                }, ${heroData.overlayGradientStart || '#000000'}, ${heroData.overlayGradientEnd || '#000000'})`,
                opacity: heroData.overlayOpacity || 0.7
              } : {})
            }} />
          )}

          {/* Legacy support: if no overlay type but backgroundType is image, apply default gradient */}
          {heroData.backgroundType === 'image' && !heroData.overlayType && (
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/90 to-purple-600/90" />
          )}

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 lg:py-20 min-h-[400px] sm:min-h-[500px] flex items-center">
            <div className={`w-full flex flex-col md:flex-row items-center ${heroData.eventImage ? 'justify-center md:justify-between' : 'justify-center'} gap-8`}>
              {/* Left column: Hero content */}
              <div className={`relative text-center ${heroData.eventImage ? 'md:text-left' : ''} max-w-xl`}>
                {/* Hero details background card */}
                {heroData.heroDetailsBackgroundCard && (
                  <div
                    className="absolute inset-0 rounded-2xl backdrop-blur-sm"
                    style={{
                      backgroundColor: heroData.heroDetailsCardBackgroundColor,
                      opacity: heroData.heroDetailsCardOpacity || 0.7,
                    }}
                  />
                )}
                <div className={`relative ${heroData.heroDetailsBackgroundCard ? 'p-6 md:p-8' : ''}`}>
                {/* Mobile event image - shown above title */}
                {heroData.eventImage && (
                  <div className="md:hidden mb-6 mx-auto max-w-sm h-48 flex items-center justify-center">
                    <img
                      src={heroData.eventImage || 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?auto=format&fit=crop&q=80&w=1200&h=600'}
                      alt={eventDetails.name}
                      className="max-w-full max-h-48 w-auto h-auto object-contain rounded-xl shadow-lg"
                      onError={(e) => {
                        console.error('Failed to load event image (mobile):', heroData.eventImage);
                        (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?auto=format&fit=crop&q=80&w=1200&h=600';
                      }}
                    />
                  </div>
                )}

                <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold hero-title">
                  {heroData.title}
                </h1>
                <p className="mt-4 text-xl hero-subtitle" dangerouslySetInnerHTML={{ __html: heroData.subtitle || '' }} />

                {/* Combined date, time, location, and timezone container */}
                {(heroData.showDate || heroData.showTime || heroData.showLocation) && !eventDetails.hideEventDates && (
                  <div className={`mt-6 mb-6 ${heroData.eventImage ? 'md:items-start' : ''} items-center inline-flex`}>
                    <EventDateLocationDisplay
                      startsAt={eventDetails.startsAt}
                      endsAt={eventDetails.endsAt}
                      showStartTime={eventDetails.showStartTime}
                      showEndTime={eventDetails.showEndTime}
                      timeZone={eventDetails.ianaTimeZone || eventDetails.timeZone}
                      location={eventDetails.location}
                      showDate={heroData.showDate}
                      showTime={heroData.showTime}
                      showLocation={heroData.showLocation}
                      showTimezone={heroData.showTimezone}
                      textColor={heroData.textColor}
                      onClick={() => setShowEventDetailsModal(true)}
                    />
                  </div>
                )}
                {/* Show only location when dates are hidden */}
                {heroData.showLocation && eventDetails.hideEventDates && eventDetails.location && (
                  <div className={`mt-6 mb-6 ${heroData.eventImage ? 'md:items-start' : ''} items-center inline-flex`}>
                    <EventDateLocationDisplay
                      startsAt={null}
                      endsAt={null}
                      showStartTime={false}
                      showEndTime={false}
                      timeZone={eventDetails.ianaTimeZone || eventDetails.timeZone}
                      location={eventDetails.location}
                      showDate={false}
                      showTime={false}
                      showLocation={true}
                      showTimezone={false}
                      textColor={heroData.textColor}
                      onClick={() => setShowEventDetailsModal(true)}
                    />
                  </div>
                )}

                {/* Keywords in hero - show up to 4 keywords */}
                {eventDetails.keywords && eventDetails.keywords.length > 0 && (
                  <div className={`flex flex-wrap gap-2 mt-4 mb-4 ${heroData.eventImage ? 'md:justify-start' : ''} justify-center`}>
                    {eventDetails.keywords.slice(0, 4).map((keyword) => {
                      // Parse the hex color to RGB for rgba usage
                      const hexToRgb = (hex: string) => {
                        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
                        return result ? {
                          r: parseInt(result[1], 16),
                          g: parseInt(result[2], 16),
                          b: parseInt(result[3], 16)
                        } : { r: 255, g: 255, b: 255 };
                      };
                      const rgb = hexToRgb(heroData.textColor || '#ffffff');

                      return (
                        <span
                          key={keyword.id}
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium backdrop-blur-sm"
                          style={{
                            backgroundColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.25)`,
                            color: heroData.textColor || '#ffffff',
                            border: `1px solid rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.40)`
                          }}
                        >
                          {keyword.displayName}
                        </span>
                      );
                    })}
                    {eventDetails.keywords.length > 4 && (() => {
                      // Parse the hex color to RGB for rgba usage
                      const hexToRgb = (hex: string) => {
                        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
                        return result ? {
                          r: parseInt(result[1], 16),
                          g: parseInt(result[2], 16),
                          b: parseInt(result[3], 16)
                        } : { r: 255, g: 255, b: 255 };
                      };
                      const rgb = hexToRgb(heroData.textColor || '#ffffff');

                      return (
                        <button
                          onClick={() => setShowKeywordsModal(true)}
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium backdrop-blur-sm transition-all hover:scale-105"
                          style={{
                            backgroundColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.25)`,
                            color: heroData.textColor || '#ffffff',
                            border: `1px solid rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.40)`
                          }}
                        >
                          +{eventDetails.keywords.length - 4} more
                        </button>
                      );
                    })()}
                  </div>
                )}

                {/* CTA Buttons */}
                {(heroData.ctaEnabled && heroData.ctaLink) || isParticipant ? (
                  <div className={`mt-2 flex ${heroData.eventImage ? 'md:justify-start' : ''} justify-center gap-4 flex-wrap`}>
                    {/* Show Enter Event button based on ctaLinkType and participant status */}
                    {isParticipant && heroData.ctaLinkType === 'registration' ? (
                      // Replace the registration CTA with Enter Event button
                      <a href={`/accounts/events/${eventSlug}`} className="cta-button inline-flex items-center gap-2 px-8 py-4 rounded-lg font-semibold transition-colors shadow-lg">
                        Enter {eventDetails?.team?.eventName || 'Event'}
                        <ArrowRight className="w-5 h-5" />
                      </a>
                    ) : (
                      <>
                        {/* Show regular CTA for non-participants or when ctaLinkType is not registration */}
                        {heroData.ctaEnabled && heroData.ctaLink && (heroData.ctaLinkType !== 'registration' || (heroData.ctaLinkType === 'registration' && eventDetails?.registerable)) ? (
                          heroData.ctaNeedsHref ? (
                            <a href={heroData.ctaLink} className="cta-button inline-flex items-center gap-2 px-8 py-4 rounded-lg font-semibold transition-colors shadow-lg">
                              {heroData.ctaText}
                              <ArrowRight className="w-5 h-5" />
                            </a>
                          ) : (
                            <Link to={heroData.ctaLink} className="cta-button inline-flex items-center gap-2 px-8 py-4 rounded-lg font-semibold transition-colors shadow-lg">
                              {heroData.ctaText}
                              <ArrowRight className="w-5 h-5" />
                            </Link>
                          )
                        ) : null}

                        {/* Show Enter Event as additional button when participant and ctaLinkType is not registration */}
                        {isParticipant && heroData.ctaLinkType !== 'registration' && (
                          <a
                            href={`/accounts/events/${eventSlug}`}
                            className="cta-button inline-flex items-center gap-2 px-8 py-4 rounded-lg font-semibold transition-colors shadow-lg relative"
                            style={{
                              // Use same styling as primary CTA but with subtle difference
                              backgroundColor: heroData.buttonBgColor || contextStyle?.headerColor || '#3B82F6',
                              color: heroData.buttonTextColor || '#ffffff',
                              // Add a subtle indicator that this is for participants
                              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05), inset 0 0 0 2px rgba(255, 255, 255, 0.2)'
                            }}
                          >
                            <Users className="w-4 h-4" />
                            Enter {eventDetails?.team?.eventName || 'Event'}
                            <ArrowRight className="w-5 h-5" />
                          </a>
                        )}
                      </>
                    )}

                    {heroData.showSecondaryCta && heroData.secondaryCtaText && heroData.secondaryCtaLink && (
                      <Link to={heroData.secondaryCtaLink} className="secondary-cta-button inline-flex items-center gap-2 px-8 py-4 rounded-lg font-semibold transition-colors shadow-lg">
                        {heroData.secondaryCtaText}
                        <ArrowRight className="w-5 h-5" />
                      </Link>
                    )}
                  </div>
                ) : null}
                </div>
              </div>

              {/* Right column: Event image - 2:1 ratio rectangle */}
              { heroData.eventImage && (
                <div className="hidden md:flex md:w-[450px] lg:w-[500px] h-[225px] lg:h-[250px] items-center justify-center">
                  <img
                    src={heroData.eventImage || 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?auto=format&fit=crop&q=80&w=1200&h=600'}
                    alt={eventDetails.name}
                    className="max-w-[450px] lg:max-w-[500px] max-h-[225px] lg:max-h-[250px] w-auto h-auto object-contain rounded-lg shadow-lg event-image"
                    onError={(e) => {
                      console.error('Failed to load event image:', heroData.eventImage);
                      (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?auto=format&fit=crop&q=80&w=1200&h=600';
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 -mt-6">

          {/* Content sections */}
          <div className="mt-12 space-y-8 pb-32">
            {sectionGroups.map((group, groupIndex) => {
              // If this is a single fullWidth section
              if (group.length === 1 && group[0].fullWidth) {
                return (
                  <div key={group[0].id}>
                    <SectionRenderer section={group[0]} />
                  </div>
                );
              }
              // If this is a pair of non-fullWidth sections or a single non-fullWidth section
              return (
                <div key={`group-${groupIndex}`} className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {group.map(section => (
                    <div key={section.id} className="flex w-full">
                      <SectionRenderer section={section} />
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile footer */}
        {((heroData.ctaEnabled && heroData.ctaLink) || isParticipant) && (
          <div className={`md:hidden fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg transform transition-transform duration-300 ${showMobileFooter ? 'translate-y-0' : 'translate-y-full'}`}>
            <div className="p-4 space-y-2">
              {/* Show Enter Event button based on ctaLinkType and participant status */}
              {isParticipant && heroData.ctaLinkType === 'registration' ? (
                // Replace the registration CTA with Enter Event button
                <a
                  href={`/accounts/events/${eventSlug}`}
                  className="flex items-center justify-center gap-2 w-full px-6 py-3 rounded-lg transition-colors font-semibold"
                  style={{
                    backgroundColor: heroData.buttonBgColor || '#2563eb',
                    color: heroData.buttonTextColor || '#ffffff'
                  }}
                >
                  Enter {eventDetails?.team?.eventName || 'Event'}
                  <ArrowRight className="w-5 h-5" />
                </a>
              ) : (
                <>
                  {/* Show regular CTA for non-participants or when ctaLinkType is not registration */}
                  {heroData.ctaEnabled && heroData.ctaLink && (heroData.ctaLinkType !== 'registration' || (heroData.ctaLinkType === 'registration' && eventDetails?.registerable)) ? (
                    heroData.ctaNeedsHref ? (
                      <a
                        href={heroData.ctaLink}
                        className="flex items-center justify-center gap-2 w-full px-6 py-3 rounded-lg transition-colors font-semibold"
                        style={{
                          backgroundColor: heroData.buttonBgColor || '#2563eb',
                          color: heroData.buttonTextColor || '#ffffff'
                        }}
                      >
                        {heroData.ctaText}
                        <ArrowRight className="w-5 h-5" />
                      </a>
                    ) : (
                      <Link
                        to={heroData.ctaLink}
                        className="flex items-center justify-center gap-2 w-full px-6 py-3 rounded-lg transition-colors font-semibold"
                        style={{
                          backgroundColor: heroData.buttonBgColor || '#2563eb',
                          color: heroData.buttonTextColor || '#ffffff'
                        }}
                      >
                        {heroData.ctaText}
                        <ArrowRight className="w-5 h-5" />
                      </Link>
                    )
                  ) : null}

                  {/* Show Enter Event as additional button when participant and ctaLinkType is not registration */}
                  {isParticipant && heroData.ctaLinkType !== 'registration' && (
                    <a
                      href={`/accounts/events/${eventSlug}`}
                      className="flex items-center justify-center gap-2 w-full px-6 py-3 rounded-lg transition-colors font-semibold"
                      style={{
                        backgroundColor: heroData.buttonBgColor || '#2563eb',
                        color: heroData.buttonTextColor || '#ffffff',
                        boxShadow: 'inset 0 0 0 2px rgba(255, 255, 255, 0.2)'
                      }}
                    >
                      <Users className="w-4 h-4" />
                      Enter {eventDetails?.team?.eventName || 'Event'}
                      <ArrowRight className="w-5 h-5" />
                    </a>
                  )}
                </>
              )}

              {heroData.showSecondaryCta && heroData.secondaryCtaText && heroData.secondaryCtaLink && (
                <Link
                  to={heroData.secondaryCtaLink}
                  className="flex items-center justify-center gap-2 w-full px-6 py-3 rounded-lg transition-colors font-semibold"
                  style={{
                    backgroundColor: heroData.secondaryButtonBgColor || 'transparent',
                    color: heroData.secondaryButtonTextColor || '#1e40af',
                    border: `2px solid ${heroData.secondaryButtonBorderColor || '#1e40af'}`
                  }}
                >
                  {heroData.secondaryCtaText}
                  <ArrowRight className="w-5 h-5" />
                </Link>
              )}
            </div>
          </div>
        )}

        {/* Keywords Modal */}
        <KeywordsModal
          keywords={eventDetails.keywords || []}
          isOpen={showKeywordsModal}
          onClose={() => setShowKeywordsModal(false)}
          primaryColor={contextStyle?.headerColor || eventDetails.style?.headerColor}
        />

        {/* Event Details Modal */}
        {eventDetails && (
          <EventDetailsModal
            event={eventDetails}
            isOpen={showEventDetailsModal}
            onClose={() => setShowEventDetailsModal(false)}
            primaryColor={contextStyle?.headerColor || eventDetails.style?.headerColor}
          />
        )}
      </div>
    </>
  );
};